import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { buildApiUrl, API_CONFIG } from '../config';
import { getAccessTokenAsync, isTokenValid } from '../utils/auth';
import { getCacheManager } from '../utils/cacheManager';
import { getTutorialManager } from '../utils/tutorialManager';
import { getTourManager } from '../utils/tourManager';
import { canLogin, clearPermissionsCache, debugTokenDetails } from '../utils/permissions';
import { setTechnicalAccountFlag, clearTechnicalAccountFlag, clearRealUserIdentity } from '../utils/realUser';
import PermissionDebug from '../components/PermissionDebug';

interface Organization {
  id: number;
  name: string;
  orgID: number;
}

const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [rememberMe, setRememberMe] = useState(true);
  const [showOrgModal, setShowOrgModal] = useState(false);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<number | null>(null);
  const [tempTokens, setTempTokens] = useState<{ accessToken: string; refreshToken: string } | null>(null);
  const [tempPermissions, setTempPermissions] = useState<any>(null);
  const navigate = useNavigate();



  // Kein Login-Check mehr nötig - der Interceptor handhabt alles automatisch
  // Wenn ein Benutzer direkt zur Login-Seite navigiert, soll er dort bleiben

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.LOGIN), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });

      if (response.ok) {
        const data = await response.json();
        
        // Check if user has multiple organizations
        if (data.organizations && data.organizations.length > 1) {
          // User has multiple organizations, show selection modal
          setOrganizations(data.organizations);
          setTempTokens({ accessToken: data.accessToken, refreshToken: data.refreshToken });
          setTempPermissions(data.permissions); // Store permissions for later use
          setShowOrgModal(true);
          setLoading(false);
        } else {
          // Debug: Zeige alle verfügbaren Berechtigungen an
          console.log('🔍 Login Response Data:', data);
          
          // Check permissions from login response directly
          const hasLoginPermission = data.permissions && data.permissions['asm-login-permission'] === true;
          console.log('🔐 Login Permission Check (from response):', hasLoginPermission);
          console.log('🔐 Available permissions in response:', data.permissions);
          
          if (!hasLoginPermission) {
            // Zeige detaillierte Fehlermeldung mit verfügbaren Berechtigungen
            const availablePermissions = data.permissions ? Object.keys(data.permissions).filter(key => data.permissions[key] === true) : [];
            const errorMsg = availablePermissions.length > 0
              ? `Sie haben keine Berechtigung, sich anzumelden. Verfügbare Berechtigungen: ${availablePermissions.join(', ')}`
              : 'Sie haben keine Berechtigung, sich anzumelden. Keine Berechtigungen gefunden.';
            setError(errorMsg);
            setLoading(false);
            return;
          }

          // User has only one organization, proceed normally
          // Optimale Speicherstrategie für PWAs mit Persistenz:
          // 1. Immer in localStorage speichern (für Persistenz)
          localStorage.setItem('accessToken', data.accessToken);
          localStorage.setItem('refreshToken', data.refreshToken);
          localStorage.setItem('loggedIn', '1');
          
          // 2. Auch in sessionStorage speichern (als Backup)
          sessionStorage.setItem('accessToken', data.accessToken);
          sessionStorage.setItem('refreshToken', data.refreshToken);
          sessionStorage.setItem('loggedIn', '1');
          
          // Debug: Zeige Token-Details
          await debugTokenDetails();
          
          // Technischer Account erkennen (typeID === 2) und Flag speichern
          try {
            const jwtPayload = JSON.parse(atob(data.accessToken.split('.')[1]));
            const isTech = jwtPayload.typeID === 2;
            const userTypeLabels: Record<number, string> = { 1: 'Persönlich', 2: 'Technisch (Shared Device)' };
            const typeLabel = userTypeLabels[jwtPayload.typeID] || `Unbekannt (${jwtPayload.typeID})`;
            
            console.log(`👤 Eingeloggter User-Typ: typeID=${jwtPayload.typeID} → ${typeLabel}`);
            
            setTechnicalAccountFlag(isTech);
            if (isTech) {
              console.log('🔧 Technischer Account erkannt (typeID=2) – Benutzer-Identifikation erforderlich');
              clearRealUserIdentity();
            }
          } catch (e) {
            console.warn('⚠️ Konnte technischen Account nicht erkennen:', e);
          }
          
          // Store permissions from login response
          if (data.permissions) {
            localStorage.setItem('userPermissions', JSON.stringify({
              permissions: Object.keys(data.permissions).filter(key => data.permissions[key] === true),
              roles: data.roles || [],
              cachedAt: Date.now()
            }));
            console.log('🔐 Stored permissions from login response:', Object.keys(data.permissions).filter(key => data.permissions[key] === true));
          }
          
          // Refresh-Versuche zurücksetzen bei erfolgreichem Login
          localStorage.removeItem('refreshAttempts');
          
          // Token-Manager starten
          const initTokenManager = async () => {
            try {
              const { getTokenManager } = await import('../utils/tokenManager');
              getTokenManager().initialize();
            } catch (error) {
              console.error('Failed to initialize token manager:', error);
            }
          };
          
          initTokenManager();
          
          // Tutorial-Check nach dem Login
          const checkTutorial = async () => {
            try {
              const tutorialManager = getTutorialManager();
              const needsTutorial = await tutorialManager.checkIfTutorialNeeded();
              
              if (needsTutorial) {
                console.log('🎯 Tutorial wird nach Login benötigt - starte automatisch');
                // Tutorial wird automatisch gestartet, wenn der Benutzer eingeloggt ist
                // Setze einen Flag, dass das Tutorial benötigt wird
                localStorage.setItem('tutorialNeeded', 'true');
                console.log('🚀 Tutorial-Flag gesetzt: tutorialNeeded = true');
              } else {
                console.log('✅ Tutorial nach Login nicht benötigt - alle Daten vorhanden');
                localStorage.removeItem('tutorialNeeded');
              }
            } catch (error) {
              console.error('Fehler beim Tutorial-Check nach Login:', error);
            }
          };
          
          checkTutorial();
          
          // Tour-Check nach dem Login (nur beim Login, nicht bei Navigation/Reload)
          const checkTour = async () => {
            try {
              console.log('🔍 Login: Prüfe Tour-Status nach Login...');
              const tourManager = getTourManager();
              const needsTour = await tourManager.checkIfTourNeeded();
              
              console.log('🔍 Login: Tour-Status-Check Ergebnis:', needsTour);
              
              if (needsTour) {
                console.log('🎯 Login: Tour wird benötigt - starte direkt');
                // Tour direkt starten statt Flag zu setzen
                const { useTour } = await import('../contexts/TourContext');
                // Tour wird automatisch gestartet wenn wir zum Dashboard navigieren
                localStorage.setItem('tourNeeded', 'true');
              } else {
                console.log('✅ Login: Tour nicht benötigt');
                localStorage.removeItem('tourNeeded');
              }
            } catch (error) {
              console.error('Fehler beim Tour-Check nach Login:', error);
              localStorage.removeItem('tourNeeded');
            }
          };
          
          checkTour();
          
          // Nach erfolgreichem Login zum Dashboard
          navigate('/dashboard');
        }
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Login fehlgeschlagen');
      }
    } catch (error) {
      setError('Verbindungsfehler');
    } finally {
      setLoading(false);
    }
  };

  const handleOrgSelection = async () => {
    if (!selectedOrg || !tempTokens) return;

    // Get the selected organization
    const selectedOrganization = organizations.find(org => org.orgID === selectedOrg);
    
    if (selectedOrganization) {
      try {
        // Check permissions from temp permissions (from initial login response)
        const hasLoginPermission = tempPermissions && tempPermissions['asm-login-permission'] === true;
        console.log('🔐 Login Permission Check (Org Selection from temp):', hasLoginPermission);
        console.log('🔐 Available permissions in temp (Org Selection):', tempPermissions);
        
        if (!hasLoginPermission) {
          // Zeige detaillierte Fehlermeldung mit verfügbaren Berechtigungen
          const availablePermissions = tempPermissions ? Object.keys(tempPermissions).filter(key => tempPermissions[key] === true) : [];
          const errorMsg = availablePermissions.length > 0
            ? `Sie haben keine Berechtigung, sich anzumelden. Verfügbare Berechtigungen: ${availablePermissions.join(', ')}`
            : 'Sie haben keine Berechtigung, sich anzumelden. Keine Berechtigungen gefunden.';
          setError(errorMsg);
          setLoading(false);
          return;
        }

        // Call the final login endpoint with the selected organization
        const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.LOGIN_FINAL), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            username, 
            password, 
            selectedOrgID: selectedOrganization.orgID 
          })
        });

        if (response.ok) {
          const data = await response.json();
          
          // Debug: Prüfe was vom Backend kommt
          console.log('🔍 Login-Response vom Backend (Org Selection):', data);
          console.log('🔍 accessToken vorhanden:', !!data.accessToken);
          console.log('🔍 refreshToken vorhanden:', !!data.refreshToken);
          console.log('🔍 accessToken Länge:', data.accessToken ? data.accessToken.length : 'undefined');
          console.log('🔍 refreshToken Länge:', data.refreshToken ? data.refreshToken.length : 'undefined');
          console.log('🔍 Verfügbare Cookies nach Login:', document.cookie);
          
          // Prüfe ob Tokens gültig sind
          if (!data.accessToken) {
            console.error('❌ Kein Access-Token vom Backend erhalten');
            setError('Kein Access-Token vom Server erhalten');
            return;
          }
          
          if (!data.refreshToken) {
            console.error('❌ Kein Refresh-Token vom Backend erhalten');
            setError('Kein Refresh-Token vom Server erhalten');
            return;
          }
          
          // Cache bei Organisationswechsel löschen
          const cacheManager = getCacheManager();
          cacheManager.setCurrentOrgID(selectedOrganization.orgID);

          // Store the final tokens
          // Optimale Speicherstrategie für PWAs mit Persistenz:
          // 1. Immer in localStorage speichern (für Persistenz)
          localStorage.setItem('accessToken', data.accessToken);
          localStorage.setItem('refreshToken', data.refreshToken);
          localStorage.setItem('loggedIn', '1');
          localStorage.setItem('selectedOrgID', selectedOrganization.orgID.toString());
          
          // Debug: Zeige Token-Details
          await debugTokenDetails();
          
          // Technischer Account erkennen (typeID === 2) und Flag speichern (Org Selection)
          try {
            const jwtPayload = JSON.parse(atob(data.accessToken.split('.')[1]));
            const isTech = jwtPayload.typeID === 2;
            const userTypeLabels: Record<number, string> = { 1: 'Persönlich', 2: 'Technisch (Shared Device)' };
            const typeLabel = userTypeLabels[jwtPayload.typeID] || `Unbekannt (${jwtPayload.typeID})`;
            
            console.log(`👤 Eingeloggter User-Typ (Org): typeID=${jwtPayload.typeID} → ${typeLabel}`);
            
            setTechnicalAccountFlag(isTech);
            if (isTech) {
              console.log('🔧 Technischer Account erkannt (Org, typeID=2) – Benutzer-Identifikation erforderlich');
              clearRealUserIdentity();
            }
          } catch (e) {
            console.warn('⚠️ Konnte technischen Account nicht erkennen (Org):', e);
          }
          
          // Store permissions from login response
          if (data.permissions) {
            localStorage.setItem('userPermissions', JSON.stringify({
              permissions: Object.keys(data.permissions).filter(key => data.permissions[key] === true),
              roles: data.roles || [],
              cachedAt: Date.now()
            }));
            console.log('🔐 Stored permissions from login response (Org Selection):', Object.keys(data.permissions).filter(key => data.permissions[key] === true));
          }
          
          // 2. Auch in sessionStorage speichern (als Backup)
          sessionStorage.setItem('accessToken', data.accessToken);
          sessionStorage.setItem('refreshToken', data.refreshToken);
          sessionStorage.setItem('loggedIn', '1');
          sessionStorage.setItem('selectedOrgID', selectedOrganization.orgID.toString());
          
          console.log('✅ Alle Tokens gespeichert');
          
          // Debug: Prüfe was gespeichert wurde
          console.log('💾 Gespeichert in localStorage:');
          console.log('💾 accessToken:', localStorage.getItem('accessToken') ? 'JA' : 'NEIN');
          console.log('💾 refreshToken:', localStorage.getItem('refreshToken') ? 'JA' : 'NEIN');
          console.log('💾 Gespeicherter refreshToken:', localStorage.getItem('refreshToken'));
          
          // Clear temporary data
          setTempTokens(null);
          setOrganizations([]);
          setSelectedOrg(null);
          setShowOrgModal(false);
          
          // Token-Manager starten
          const initTokenManager = async () => {
            try {
              const { getTokenManager } = await import('../utils/tokenManager');
              getTokenManager().initialize();
            } catch (error) {
              console.error('Failed to initialize token manager:', error);
            }
          };
          
          initTokenManager();
          
          // Tutorial-Check nach dem Login
          const checkTutorial = async () => {
            try {
              const tutorialManager = getTutorialManager();
              const needsTutorial = await tutorialManager.checkIfTutorialNeeded();
              
              if (needsTutorial) {
                console.log('🎯 Tutorial wird nach Login benötigt - starte automatisch');
                // Tutorial wird automatisch gestartet, wenn der Benutzer eingeloggt ist
                // Setze einen Flag, dass das Tutorial benötigt wird
                localStorage.setItem('tutorialNeeded', 'true');
                console.log('🚀 Tutorial-Flag gesetzt: tutorialNeeded = true');
              } else {
                console.log('✅ Tutorial nach Login nicht benötigt - alle Daten vorhanden');
                localStorage.removeItem('tutorialNeeded');
              }
            } catch (error) {
              console.error('Fehler beim Tutorial-Check nach Login:', error);
            }
          };
          
          checkTutorial();
          
          // Nach erfolgreichem Login zum Dashboard
          navigate('/dashboard');
        } else {
          const errorData = await response.json();
          setError(errorData.message || 'Login fehlgeschlagen');
          setShowOrgModal(false);
          setTempTokens(null);
          setOrganizations([]);
          setSelectedOrg(null);
        }
      } catch (error) {
        setError('Verbindungsfehler');
        setShowOrgModal(false);
        setTempTokens(null);
        setOrganizations([]);
        setSelectedOrg(null);
      }
    }
  };



  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: 'calc(100vh - 56px)', // 56px für die Navbar
        background: '#fafbfc',
        minHeight: 320,
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 16,
          padding: 32,
          minWidth: 340,
          boxShadow: '0 4px 32px #0002',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          maxWidth: 400,
          boxSizing: 'border-box',
          margin: '0 auto',
        }}
        className="login-card"
      >
        <h2 style={{ marginBottom: 24, letterSpacing: 1 }}>Login</h2>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 18, minWidth: 260, width: 260 }}>
          <input
            type="text"
            placeholder="Benutzername"
            value={username}
            onChange={e => setUsername(e.target.value)}
            onFocus={(e) => {
              // iOS fix: Ensure input is visible when keyboard appears
              setTimeout(() => {
                e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }, 300);
            }}
            style={{
              fontSize: 16, // iOS: Prevent zoom on focus
              padding: '10px 14px',
              borderRadius: 10,
              border: '2px solid #ddd',
              outline: 'none',
              minWidth: 180,
              maxWidth: 320,
              textAlign: 'center',
              marginBottom: 4,
              boxShadow: '0 2px 8px #eee',
              WebkitAppearance: 'none', // iOS: Remove default styling
              WebkitBorderRadius: '10px', // iOS: Ensure border radius works
            }}
            required
          />
          <input
            type="password"
            placeholder="Passwort"
            value={password}
            onChange={e => setPassword(e.target.value)}
            onFocus={(e) => {
              // iOS fix: Ensure input is visible when keyboard appears
              setTimeout(() => {
                e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }, 300);
            }}
            style={{
              fontSize: 16, // iOS: Prevent zoom on focus
              padding: '10px 14px',
              borderRadius: 10,
              border: '2px solid #ddd',
              outline: 'none',
              minWidth: 180,
              maxWidth: 320,
              textAlign: 'center',
              marginBottom: 4,
              boxShadow: '0 2px 8px #eee',
              WebkitAppearance: 'none', // iOS: Remove default styling
              WebkitBorderRadius: '10px', // iOS: Ensure border radius works
            }}
            required
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
            <input
              type="checkbox"
              id="rememberMe"
              checked={rememberMe}
              onChange={e => setRememberMe(e.target.checked)}
              style={{ width: 16, height: 16 }}
            />
            <label htmlFor="rememberMe" style={{ fontSize: 14, color: '#555' }}>
              Angemeldet bleiben
            </label>
          </div>
          <button
            type="submit"
            disabled={loading}
            style={{
              background: loading ? '#ccc' : '#c00',
              color: '#fff',
              border: 'none',
              borderRadius: 12,
              padding: '14px 0',
              fontWeight: 'bold',
              fontSize: 18,
              minWidth: 100,
              cursor: loading ? 'not-allowed' : 'pointer',
              boxShadow: '0 2px 8px #c002',
              marginTop: 8,
              letterSpacing: 1,
              transition: 'background 0.2s',
            }}
          >
            {loading ? 'Anmelden...' : 'Login'}
          </button>
          {error && <div style={{ color: '#c00', marginTop: 8, textAlign: 'center', fontWeight: 500 }}>{error}</div>}
        </form>
      </div>

      {/* Organization Selection Modal */}
      {showOrgModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 16,
            padding: 32,
            minWidth: 400,
            maxWidth: 500,
            boxShadow: '0 4px 32px #0002',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}>
            <h3 style={{ color: '#c00', fontWeight: 900, fontSize: 22, marginBottom: 16 }}>
              Organisation auswählen
            </h3>
            <p style={{ textAlign: 'center', marginBottom: 24, color: '#666' }}>
              Du gehörst mehreren Organisationen an. Bitte wähle die Organisation aus, für die Du arbeiten möchtest.
            </p>
            
            <div style={{ width: '100%', marginBottom: 24 }}>
              {organizations.map((org) => (
                <div
                  key={org.orgID}
                  style={{
                    padding: '12px 16px',
                    border: selectedOrg === org.orgID ? '2px solid #c00' : '2px solid #ddd',
                    borderRadius: 8,
                    marginBottom: 8,
                    cursor: 'pointer',
                    backgroundColor: selectedOrg === org.orgID ? '#fff5f5' : '#fff',
                    transition: 'all 0.2s'
                  }}
                  onClick={() => setSelectedOrg(org.orgID)}
                >
                  <div style={{ fontWeight: 600, fontSize: 16 }}>{org.name}</div>
                </div>
              ))}
            </div>
            
            <div style={{ display: 'flex', gap: 12, width: '100%' }}>
              <button
                onClick={() => {
                  setShowOrgModal(false);
                  setTempTokens(null);
                  setOrganizations([]);
                  setSelectedOrg(null);
                }}
                style={{
                  flex: 1,
                  background: '#666',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  padding: '12px',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Abbrechen
              </button>
              <button
                onClick={handleOrgSelection}
                disabled={!selectedOrg}
                style={{
                  flex: 1,
                  background: selectedOrg ? '#c00' : '#ccc',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  padding: '12px',
                  fontWeight: 600,
                  cursor: selectedOrg ? 'pointer' : 'not-allowed'
                }}
              >
                Bestätigen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Debug component - only show if there are stored permissions */}
      {localStorage.getItem('userPermissions') ? (
        <PermissionDebug />
      ) : null}
      <style>{`
@media (max-width: 600px) {
  .login-card {
    min-width: unset !important;
    max-width: unset !important;
    width: 80% !important;
    margin-left: 10% !important;
    margin-right: 10% !important;
    padding-left: 8vw !important;
    padding-right: 8vw !important;
  }
}`}</style>
    </div>
  );
};

export default Login; 